// Modal: upload a file (CSV / XLSX / JSON / NDJSON / ZIP / GZ / TAR /
// PDF + binary) → events on the source. Two modes:
//   - parse-and-ingest (default): rows become events, run through triggers
//   - store-as-attachment (mode=store): single event with metadata pointer,
//     file lives in GCS as-is. Useful for PDFs / ODF / contracts.
//
// Auto-routing: detectFormat() inspects filename + MIME. Non-parseable
// formats (PDF, image, ODF) auto-flip to store mode. Binary text-unsafe
// formats (XLSX/ZIP/GZ/TAR) skip client-side row counting and let the
// server handle decompression + parsing.
//
// Auto-bulk: ZIP/GZ/TAR/XLSX use ?bulk=true to batch BQ MERGEs (cheaper
// at scale).
//
// Extracted from pages/SourceDetailPage.tsx as part of the file split
// (Apr 2026).

import { useState } from 'react';
import { useToast } from '../Toast';
import { Label } from '../common/atoms';

export function UploadDialog({ sourceId, onClose }: { sourceId: string; onClose: () => void }) {
  const toast = useToast();
  const [file, setFile] = useState<File | null>(null);
  const [storeMode, setStoreMode] = useState(false);  // attachment storage, no parsing
  const [format, setFormat] = useState<'json' | 'csv' | 'xlsx' | 'xls' | 'zip' | 'gz' | 'tar' | 'ndjson' | null>(null);
  const [parsedCount, setParsedCount] = useState<number | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ accepted?: number; errors?: { index: number; error: string }[]; mode?: string; download_url?: string; gcs_path?: string; signed_url_24h?: string } | null>(null);

  /** Detect file format from name + MIME. Returns null when nothing
   *  looks parseable (PDF, image, ODF, …) so the caller can route to
   *  store-mode automatically. */
  function detectFormat(f: File): 'json' | 'csv' | 'xlsx' | 'xls' | 'zip' | 'gz' | 'tar' | 'ndjson' | null {
    const name = f.name.toLowerCase();
    if (/\.xlsx$/.test(name) || /openxmlformats-officedocument\.spreadsheetml/i.test(f.type)) return 'xlsx';
    if (/\.xls$/.test(name)  || /vnd\.ms-excel/i.test(f.type)) return 'xls';
    if (/\.zip$/.test(name)  || /application\/zip/i.test(f.type)) return 'zip';
    if (/\.tar$/.test(name)  || /application\/x-tar/i.test(f.type)) return 'tar';
    if (/\.gz$/.test(name)   || /application\/gzip/i.test(f.type)) return 'gz';
    if (/\.csv$/.test(name)  || /^text\/csv/i.test(f.type)) return 'csv';
    if (/\.(ndjson|jsonl)$/.test(name) || /application\/x-ndjson/i.test(f.type)) return 'ndjson';
    if (/\.json$/.test(name) || /application\/json/i.test(f.type)) return 'json';
    return null;
  }

  async function handleFile(f: File | null): Promise<void> {
    setFile(f); setParsedCount(null); setParseError(null); setResult(null); setFormat(null);
    if (!f) return;
    if (f.size > 25 * 1024 * 1024) { setParseError(`File too large: ${(f.size/1024/1024).toFixed(2)}MB (max 25MB)`); return; }

    const fmt = detectFormat(f);
    // Non-parseable file (PDF / image / ODF) → suggest store mode.
    if (!fmt && !storeMode) {
      setStoreMode(true);
    }
    setFormat(fmt);

    if (storeMode || !fmt) {
      setParsedCount(1); // 1 event with metadata payload
      return;
    }

    // Binary formats (XLSX, XLS, ZIP, GZ, TAR): never read as text — they
    // are not UTF-8 and JSON.parse on the bytes throws garbage like
    // "Unexpected token 'P', \"PK\"...". Server-side parser handles them
    // via the /upload route's decompress + parsers pipeline. Show row
    // count as '?' since we can't infer it client-side.
    if (fmt === 'xlsx' || fmt === 'xls' || fmt === 'zip' || fmt === 'gz' || fmt === 'tar') {
      setParsedCount(0); // sentinel — UI shows '?' for binary formats
      return;
    }

    const text = await f.text();
    if (fmt === 'csv') {
      const lines = text.split(/\r?\n/).filter(l => l.trim() !== '');
      if (lines.length < 2) { setParseError('CSV must have a header row + at least one data row'); return; }
      setParsedCount(lines.length - 1); return;
    }
    if (fmt === 'ndjson') {
      const lines = text.split(/\r?\n/).filter(l => l.trim() !== '');
      if (lines.length === 0) { setParseError('NDJSON must have at least one row'); return; }
      setParsedCount(lines.length); return;
    }
    try {
      const parsed = JSON.parse(text);
      if (Array.isArray(parsed)) setParsedCount(parsed.length);
      else if (parsed && typeof parsed === 'object') setParsedCount(1);
      else { setParseError('JSON root must be an object or array of objects'); return; }
    } catch (e) {
      setParseError(`Invalid JSON: ${(e as Error).message}. If this isn't event data, switch to "Store as attachment" mode.`);
    }
  }

  /** Resolve outbound Content-Type for the upload POST. Server's parser
   *  uses CT + filename + magic bytes, so any of these three signals lets
   *  it route correctly — but we send the most specific one we know. */
  function uploadContentType(): string {
    if (storeMode) return file?.type || 'application/octet-stream';
    switch (format) {
      case 'csv':   return 'text/csv';
      case 'xlsx':  return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      case 'xls':   return 'application/vnd.ms-excel';
      case 'zip':   return 'application/zip';
      case 'gz':    return 'application/gzip';
      case 'tar':   return 'application/x-tar';
      case 'ndjson': return 'application/x-ndjson';
      case 'json':  return 'application/json';
      default:      return file?.type || 'application/octet-stream';
    }
  }

  async function handleUpload(): Promise<void> {
    if (!file) return;
    setSubmitting(true); setResult(null); setParseError(null);
    try {
      // Auto-bulk for XLSX/ZIP/GZ/TAR (large multi-row inputs benefit from
      // batched MERGE; small ones still work fine in non-bulk).
      const useBulk = !storeMode && (format === 'xlsx' || format === 'xls' || format === 'zip' || format === 'gz' || format === 'tar');
      const url = storeMode
        ? `/upload/${sourceId}?mode=store`
        : useBulk ? `/upload/${sourceId}?bulk=true` : `/upload/${sourceId}`;
      const buf = await file.arrayBuffer();
      const ct = uploadContentType();
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': ct, 'X-Filename': file.name },
        body: buf,
      });
      const body = await res.json() as { mode?: string; accepted?: number; errors?: { index: number; error: string }[]; error?: { code: string; detail?: string }; gcs_path?: string; download_url?: string; signed_url_24h?: string };
      if (!res.ok) {
        setParseError(body.error?.detail ?? body.error?.code ?? `HTTP ${res.status}`);
      } else {
        setResult(body);
      }
    } catch (e) {
      setParseError(String(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 50, background: 'oklch(0.18 0.01 250 / 0.4)', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: 80 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ width: 'min(560px, calc(100vw - 48px))', background: 'var(--bg-elev)', border: '1px solid var(--line)', borderRadius: 12, padding: 24 }}>
        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>Upload a file</div>
        <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 14, lineHeight: 1.5 }}>
          {storeMode
            ? <>Store-as-attachment: file is stored as-is. ONE event lands in this Source with payload <code style={{ fontFamily: 'var(--mono)' }}>{'{ filename, gcs_path, download_url, size_bytes, … }'}</code> — perfect for PDFs, contracts, invoices, ODF docs you want to <strong>reference</strong> from your tables.</>
            : <>Parse-and-ingest: JSON / CSV / ZIP / XLSX / tar.gz. Rows become events through your active triggers. For non-data files (PDF, image, contract) flip to <strong>Store as attachment</strong> below.</>
          }
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, fontSize: 13, color: 'var(--ink-2)', cursor: 'pointer', padding: '10px 12px', background: 'var(--bg-sunken)', borderRadius: 6, border: '1px solid var(--line)' }}>
          <input type="checkbox" checked={storeMode} onChange={e => { setStoreMode(e.target.checked); if (file) void handleFile(file); }} />
          <span><strong>Store as attachment</strong> — no parsing, just keep the file + metadata</span>
        </label>

        <Label>File</Label>
        <input
          type="file"
          accept={storeMode ? undefined : '.json,.ndjson,.jsonl,.csv,.tsv,.xls,.xlsx,.zip,.gz,.tar,application/json,text/csv,application/zip'}
          onChange={e => { void handleFile(e.target.files?.[0] ?? null); }}
          style={{ width: '100%', padding: '10px 12px', fontSize: 13, fontFamily: 'var(--ui)', background: 'var(--bg-sunken)', border: '1px solid var(--line)', borderRadius: 6, outline: 'none' }}
        />

        {file && (
          <div style={{ marginTop: 6, fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--ink-3)' }}>
            {file.name} · {file.type || '(no content-type)'} · {(file.size/1024).toFixed(1)} KB
          </div>
        )}

        {/* Pre-upload preview banner. Binary formats (XLSX/ZIP/GZ/TAR)
            can't be counted client-side — bytes are not UTF-8 — so we
            show 'server-side parsing' instead of '0 events' which read
            as 'nothing to upload'. Hidden once the server response
            (result) lands so it doesn't compete with the success banner. */}
        {file && !storeMode && parsedCount !== null && !parseError && !result && (() => {
          const binary = format === 'xlsx' || format === 'xls' || format === 'zip' || format === 'gz' || format === 'tar';
          return (
            <div style={{ marginTop: 12, padding: '8px 12px', background: 'var(--accent-soft)', border: '1px solid var(--accent-line)', borderRadius: 6, fontSize: 12.5, color: 'oklch(0.42 0.12 150)' }}>
              {binary
                ? <>OK · {format?.toUpperCase()} file recognised — rows will be parsed server-side</>
                : <>OK · Parsed <strong>{parsedCount}</strong> event{parsedCount === 1 ? '' : 's'} ({format?.toUpperCase()})</>}
            </div>
          );
        })()}

        {parseError && (
          <div style={{ marginTop: 12, padding: '8px 12px', background: 'var(--bg-sunken)', border: '1px solid var(--line)', borderRadius: 6, fontSize: 12, color: 'var(--danger)' }}>
            Failed · {parseError}
          </div>
        )}

        {result && result.mode === 'store' && (
          <div style={{ marginTop: 12, padding: '10px 12px', background: 'var(--accent-soft)', border: '1px solid var(--accent-line)', borderRadius: 6, fontSize: 12.5, color: 'var(--ink)', display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div>OK · Stored as attachment. 1 event recorded with the storage pointer.</div>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--ink-2)', wordBreak: 'break-all' }}>{result.gcs_path}</div>
            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              {result.download_url && <a className="btn sm ghost" href={result.download_url} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>↓ download</a>}
              {result.signed_url_24h && <button className="btn sm ghost" onClick={() => { void navigator.clipboard.writeText(result.signed_url_24h!); toast.show('24h signed URL copied.', { kind: 'success' }); }}>copy signed URL</button>}
            </div>
          </div>
        )}

        {result && result.mode !== 'store' && typeof result.accepted === 'number' && (
          <div style={{ marginTop: 12, padding: '8px 12px', background: 'var(--accent-soft)', border: '1px solid var(--accent-line)', borderRadius: 6, fontSize: 12.5, color: 'oklch(0.42 0.12 150)' }}>
            OK · Accepted <strong>{result.accepted}</strong> event{result.accepted === 1 ? '' : 's'} — watch the Live tab.
            {(result.errors?.length ?? 0) > 0 && <span style={{ color: 'var(--danger)' }}> {result.errors!.length} row{result.errors!.length === 1 ? '' : 's'} failed.</span>}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button className="btn ghost" onClick={onClose}>{result ? 'Done' : 'Close'}</button>
          {!result && (() => {
            const binary = format === 'xlsx' || format === 'xls' || format === 'zip' || format === 'gz' || format === 'tar';
            const label = submitting
              ? 'Uploading…'
              : storeMode
                ? 'Store file'
                : binary
                  ? 'Upload file'
                  : parsedCount && parsedCount > 0
                    ? `Upload ${parsedCount} event${parsedCount === 1 ? '' : 's'}`
                    : 'Upload';
            return (
              <button className="btn primary" onClick={() => { void handleUpload(); }} disabled={submitting || !file || (!storeMode && (parsedCount === null || !!parseError))}>
                {label}
              </button>
            );
          })()}
        </div>
      </div>
    </div>
  );
}
